/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

"use client";

import { useLocation } from "@epic-mw/app-context";
import { emitEvent } from "@epic-mw/common-utils/dist/analyticsUtils";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";

import { useAccountId } from "../account";
import { useAccountLoginRequired } from "../auth";

import {
	EulaLatestRes,
	getEulaData,
	EulaAcceptedRes,
	createEulaKey,
	getEulaAccepted,
	EulaAcceptRes,
	acceptEula,
} from "./eula.fetch";

export const useEulaContent = (
	key: string,
): {
	eulaContent?: EulaLatestRes;
	isLoading: boolean;
	error?: string;
} => {
	const shouldFetch = !!key;

	const { data, error } = useSWRImmutable(shouldFetch ? key : null, getEulaData, {
		errorRetryCount: 1,
	});

	return {
		eulaContent: data,
		isLoading: !data && !error,
		error,
	};
};

export const useEulaAccepted = (
	key: string,
	locale: string,
): {
	eulaAccepted?: EulaAcceptedRes;
	isLoading: boolean;
	error?: string;
} => {
	const { accountLoading } = useAccountLoginRequired();
	const accountId = useAccountId();

	const shouldFetch = !accountLoading && key && accountId;
	const request = createEulaKey({
		key,
		accountId,
		locale,
	});

	const { data, error } = useSWR(shouldFetch ? request : null, getEulaAccepted, {
		errorRetryCount: 1,
	});

	return {
		eulaAccepted: data,
		isLoading: accountLoading || (!data && !error),
		error,
	};
};

export const useEulaAcceptance = (
	key: string,
	locale: string,
	submit: boolean,
	version?: number,
): {
	accept?: EulaAcceptRes;
	isLoading: boolean;
	error?: string;
} => {
	const { accountLoading } = useAccountLoginRequired();
	const accountId = useAccountId();
	const location = useLocation();

	const shouldFetch = !accountLoading && key && accountId && version && submit;
	const request = createEulaKey({
		key,
		accountId,
		version,
		locale,
	});

	const { data, error } = useSWRImmutable(shouldFetch ? request : null, acceptEula, {
		errorRetryCount: 1,
	});

	if (shouldFetch && data && !error) {
		emitEvent(location, {
			eventAction: "client.eula.accept",
			eventLabel: key,
		});
	}

	return {
		accept: data,
		isLoading: accountLoading || (submit && !data && !error),
		error,
	};
};
